import React, { useEffect, useState, Key, useRef } from 'react';
import { Transfer, Row, Col, Button, message, Select } from 'antd';
import useFilters from 'utils/hooks/useFilters';
import { useLoadAllocation } from 'utils/hooks/useLoadAllocation';
import { useSaveAllocation } from 'utils/hooks/useSaveAllocation';
import { entityOp } from 'store/ducks/DataEntry/Entities';
import { branchOp } from 'store/ducks/DataEntry/Branch';
import { functionOp } from 'store/ducks/DataEntry/Functions';
import { tppOp } from 'store/ducks/DataEntry/ThirdPartyProviders';
import contractualArrangementsOp from 'store/ducks/DataEntry/ContractualInformation/operations';
import {
  AllocationConfig,
  TransferConfigMap,
  AllocationItem,
  TargetConfig,
} from 'store/ducks/Global/types';
import ApiService from 'utils/ApiCalls';
import { ALLOCATION_SUCCESS_MESSAGES } from 'constants/allocationMessages';
import * as Styled from './Allocation.styled';
import { useTranslation } from 'react-i18next';

const Allocation: React.FC = () => {
  const { query } = useFilters();
  const { t } = useTranslation();

  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [parentOptions, setParentOptions] = useState<Record<string, any[]>>({});
  const [functionDataSource, setFunctionDataSource] = useState<
    Record<string, any[]>
  >({});
  const [functionTargets, setFunctionTargets] = useState<Record<string, Key[]>>(
    {},
  );
  const [branchDataSource, setBranchDataSource] = useState<
    Record<string, any[]>
  >({});
  const [branchTargets, setBranchTargets] = useState<Record<string, Key[]>>({});
  const [tpspDataSource, setTpspDataSource] = useState<Record<string, any[]>>(
    {},
  );
  const [tpspTargets, setTpspTargets] = useState<Record<string, Key[]>>({});
  const [
    contractualArrangementsDataSource,
    setContractualArrangementsDataSource,
  ] = useState<Record<string, any[]>>({});
  const [contractualArrangementsTargets, setContractualArrangementsTargets] =
    useState<Record<string, Key[]>>({});
  const [ictSystemsDataSource, setIctSystemsDataSource] = useState<
    Record<string, any[]>
  >({});
  const [ictSystemsTargets, setIctSystemsTargets] = useState<
    Record<string, Key[]>
  >({});
  const [ictServicesDataSource, setIctServicesDataSource] = useState<
    Record<string, any[]>
  >({});
  const [ictServicesTargets, setIctServicesTargets] = useState<
    Record<string, Key[]>
  >({});
  const [targets, setTargets] = useState<Record<string, Key[]>>({});
  const [dataSource, setDataSource] = useState<Record<string, any[]>>({});

  const [entityParentOptions, setEntityParentOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [branchParentOptions, setBranchParentOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [functionParentOptions, setFunctionParentOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [tpspParentOptions, setTpspParentOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [
    contractualArrangementsParentOptions,
    setContractualArrangementsParentOptions,
  ] = useState<{ value: number; label: string }[]>([]);
  const [ictSystemsParentOptions, setIctSystemsParentOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [ictServicesParentOptions, setIctServicesParentOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [selectedParents, setSelectedParents] = useState<
    Record<string, { id: number; label: string }>
  >({});

  const previousTargetsRef = useRef<Record<string, Key[]>>({});

  const createLoadCallback =
    (
      setDataSource: React.Dispatch<
        React.SetStateAction<Record<string, AllocationItem[]>>
      >,
      setTargets: React.Dispatch<React.SetStateAction<Record<string, Key[]>>>,
      key: string,
    ) =>
    (items: AllocationItem[]) => {
      const itemKeys = items.map((i) => i.key);
      setDataSource((prev) => {
        const existing = prev[key] || [];
        const mergedMap = new Map();
        [...existing, ...items].forEach((item) => {
          mergedMap.set(item.key, item);
        });
        return { ...prev, [key]: Array.from(mergedMap.values()) };
      });
      setTargets((prev) => ({
        ...prev,
        [key]: itemKeys,
      }));
      previousTargetsRef.current[key] = itemKeys;
    };

  const allocationConfigs: AllocationConfig[] = [
    // {
    //   id: 'entity',
    //   title: 'Entities → Entity Maintaining Register',
    //   type: 'entity',
    //   hasParent: true,
    //   parentOptions: entityParentOptions,
    //   parentSource: 'financial',
    // },
    // {
    //   id: 'branch',
    //   title: 'Branches → Entity',
    //   type: 'branch',
    //   hasParent: true,
    //   parentOptions: branchParentOptions,
    //   dependsOn: 'entity',
    //   parentSource: 'entity',
    // },
    {
      id: 'function',
      title: t('ALLOCATION.FUNCTIONS_ENTITY'),
      type: 'function',
      hasParent: true,
      parentOptions: functionParentOptions,
      parentSource: 'entity',
    },
    {
      id: 'tpsp',
      title: t('ALLOCATION.TPP_ENTITY'),
      type: 'tpsp',
      hasParent: true,
      parentOptions: tpspParentOptions,
      parentSource: 'entity',
    },
    {
      id: 'contractualArrangements',
      title: t('ALLOCATION.CONTRACT_TPP'),
      type: 'contractualArrangements',
      hasParent: true,
      parentOptions: contractualArrangementsParentOptions,
      parentSource: 'tpsp',
    },
    {
      id: 'ictSystems',
      title: t('ALLOCATION.ICT_STSYTEM_ENTITY'),
      type: 'ictSystems',
      hasParent: true,
      parentOptions: ictSystemsParentOptions,
      parentSource: 'entity',
    },
    {
      id: 'ictServices',
      title: t('ALLOCATION.SERVICE_CONTRACT'),
      type: 'ictServices',
      hasParent: true,
      parentOptions: ictServicesParentOptions,
      parentSource: 'contractualArrangements',
    },
  ];

  const fetchParents = async () => {
    const financialResp = await entityOp.fetchAllFinancialEntities();
    setEntityParentOptions(
      (financialResp?.data || []).map((f: any) => ({
        value: f.id,
        label: f.financial_entity_name,
      })),
    );

    const entitiesResp = await entityOp.fetchEntityNames();
    const entityOptions = (entitiesResp?.data || []).map((e: any) => ({
      value: e.id,
      label: e.name_of_entity,
    }));
    setBranchParentOptions(entityOptions);
    setFunctionParentOptions(entityOptions);
    setTpspParentOptions(entityOptions);
    setIctSystemsParentOptions(entityOptions);

    const tpspResp = await tppOp.fetchAllThirdPartyServiceProviders();
    const tpspOptions = (tpspResp?.data || []).map((t: any) => ({
      value: t.id,
      label: t.third_party_service_provider_name,
    }));
    setContractualArrangementsParentOptions(tpspOptions);

    const contractualResp =
      await contractualArrangementsOp.fetchListOfContractualArrangements();
    const contractualOptions = (contractualResp?.data || []).map((ca: any) => ({
      value: ca.id,
      label: ca.contract_name,
    }));
    setIctServicesParentOptions(contractualOptions);
  };

  const updateBranchParents = (selectedEntities: any[]) => {
    const mapped = selectedEntities.map((e) => ({
      value: e.key,
      label: e.title,
    }));

    setParentOptions((prev) => ({
      ...prev,
      branch: mapped,
    }));
  };

  const fetchDataByType = async (
    type: string,
    configId: string,
    items?: any[],
  ) => {
    try {
      let mapped: any[] = [];

      if (type === 'entity') {
        const res = await entityOp.fetchEntityNames();
        const list = res?.data || [];

        mapped = list.map((item: any) => ({
          key: String(item.id),
          title: item.name_of_entity,
          ...item,
        }));
      }

      if (type === 'branch') {
        const res = await branchOp.fetchAllBranches();
        const list = res?.data || [];

        mapped = list.map((b: any) => ({
          key: String(b.id),
          title: b.branch_name,
          ...b,
        }));
        setBranchDataSource((prev) => ({
          ...prev,
          [configId]: mapped,
        }));
        return;
      }

      if (type === 'function') {
        const res = await functionOp.fetchAllFunctions();
        const list = res?.data || [];

        mapped = list.map((f: any) => ({
          key: String(f.id),
          title: f.function_name,
          ...f,
        }));
        setFunctionDataSource((prev) => ({
          ...prev,
          [configId]: mapped,
        }));
        return;
      }

      if (type === 'tpsp') {
        const res = await tppOp.fetchAllThirdPartyServiceProviders();
        const list = res?.data || [];

        mapped = list.map((t: any) => ({
          key: String(t.id),
          title: t.third_party_service_provider_name,
          ...t,
        }));
        setTpspDataSource((prev) => ({
          ...prev,
          [configId]: mapped,
        }));
        return;
      }

      if (type === 'contractualArrangements') {
        const res =
          await contractualArrangementsOp.fetchListOfContractualArrangements();
        const list = res?.data || [];

        mapped = list.map((ca: any) => ({
          key: String(ca.id),
          title: ca.contract_name,
          ...ca,
        }));
        setContractualArrangementsDataSource((prev) => ({
          ...prev,
          [configId]: mapped,
        }));
        return;
      }

      if (type === 'ictSystems') {
        const res = await ApiService.getListOfICTSystems();
        const list = res?.data || [];

        mapped = list.map((item: any) => ({
          key: String(item.id),
          title: item.name,
          ...item,
        }));
        setIctSystemsDataSource((prev) => ({
          ...prev,
          [configId]: mapped,
        }));
        return;
      }

      if (type === 'ictServices') {
        const res = await ApiService.getListOfICTServices();
        const list = res?.data || [];

        mapped = list.map((item: any) => ({
          key: String(item.id),
          title: item.description,
          ...item,
        }));
        setIctServicesDataSource((prev) => ({
          ...prev,
          [configId]: mapped,
        }));
        return;
      }

      setDataSource((prev) => ({
        ...prev,
        [configId]: mapped,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBranchesForEntities = async (configId: string, items: any[]) => {
    try {
      const responses = await Promise.all(
        items.map((item: any) =>
          branchOp.fetchBranchbyEntityName({
            entity_id: Number(item.key),
            entity_name: item.title,
          }),
        ),
      );

      const branches = responses.flatMap((res: any) => {
        const list = res?.data || [];
        return list.map((b: any) => ({
          key: String(b.branch_id),
          title: b.branch_name,
          ...b,
        }));
      });

      setBranchDataSource((prev) => ({
        ...prev,
        [configId]: branches,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchParents();
    fetchDataByType('entity', 'entity');
    fetchDataByType('branch', 'branch');
    fetchDataByType('function', 'function');
    fetchDataByType('tpsp', 'tpsp');
    fetchDataByType('contractualArrangements', 'contractualArrangements');
    fetchDataByType('ictSystems', 'ictSystems');
    fetchDataByType('ictServices', 'ictServices');
  }, []);

  const { loadAllocation } = useLoadAllocation({
    onBranchLoad: createLoadCallback(
      setBranchDataSource,
      setBranchTargets,
      'branch',
    ),
    onFunctionLoad: createLoadCallback(
      setFunctionDataSource,
      setFunctionTargets,
      'function',
    ),
    onTpspLoad: createLoadCallback(setTpspDataSource, setTpspTargets, 'tpsp'),
    onContractualArrangementsLoad: createLoadCallback(
      setContractualArrangementsDataSource,
      setContractualArrangementsTargets,
      'contractualArrangements',
    ),
    onIctSystemsLoad: createLoadCallback(
      setIctSystemsDataSource,
      setIctSystemsTargets,
      'ictSystems',
    ),
    onIctServicesLoad: createLoadCallback(
      setIctServicesDataSource,
      setIctServicesTargets,
      'ictServices',
    ),
  });

  useEffect(() => {
    const loadData = async () => {
      const parentId = selectedParents.entity?.id;
      if (parentId) {
        await loadEntityAllocation(parentId);
      }
      const branchParentId = selectedParents.branch?.id;
      if (branchParentId) {
        await fetchDataByType('branch', 'branch');
        await loadAllocation('branch', branchParentId);
      }
      const functionParentId = selectedParents.function?.id;
      if (functionParentId) {
        await fetchDataByType('function', 'function');
        await loadAllocation('function', functionParentId);
      }
      const tpspParentId = selectedParents.tpsp?.id;
      if (tpspParentId) {
        await fetchDataByType('tpsp', 'tpsp');
        await loadAllocation('tpsp', tpspParentId);
      }
      const contractualArrangementsParentId =
        selectedParents.contractualArrangements?.id;
      if (contractualArrangementsParentId) {
        await fetchDataByType(
          'contractualArrangements',
          'contractualArrangements',
        );
        await loadAllocation(
          'contractualArrangements',
          contractualArrangementsParentId,
        );
      }
      const ictSystemsParentId = selectedParents.ictSystems?.id;
      if (ictSystemsParentId) {
        await fetchDataByType('ictSystems', 'ictSystems');
        await loadAllocation('ictSystems', ictSystemsParentId);
      }
    };
    loadData();
  }, [
    selectedParents.entity,
    selectedParents.branch,
    selectedParents.function,
    selectedParents.tpsp,
    selectedParents.contractualArrangements,
    selectedParents.ictServices,
    selectedParents.ictSystems,
    query,
  ]);

  const handleTransferChange = async (config: any, keys: Key[]) => {
    setTargets((prev) => ({ ...prev, [config.id]: keys }));

    const selectedItems = keys
      .map((key) =>
        dataSource[config.id]?.find((item: any) => item.key === key),
      )
      .filter(Boolean);

    const dependent = allocationConfigs.find((c) => c.dependsOn === config.id);
    if (dependent) {
      await fetchDataByType(dependent.type, dependent.id, selectedItems);

      if (dependent.hasParent && dependent.parentSource === 'entity') {
        updateBranchParents(selectedItems);
      }
    }
  };

  const transferConfigMap = {
    entity: {
      dataSource: dataSource['entity'] || [],
      targetKeys: targets['entity'] || [],
      setTargets: (keys: Key[]) =>
        setTargets((prev) => ({ ...prev, entity: keys })),
      onChange: (keys: Key[]) =>
        handleTransferChange(allocationConfigs[0], keys),
    },
    branch: {
      dataSource: branchDataSource['branch'] || [],
      targetKeys: branchTargets['branch'] || [],
      setTargets: (keys: Key[]) =>
        setBranchTargets((prev) => ({ ...prev, branch: keys })),
      onChange: (keys: Key[]) =>
        setBranchTargets((prev) => ({ ...prev, branch: keys })),
    },
    function: {
      dataSource: functionDataSource['function'] || [],
      targetKeys: functionTargets['function'] || [],
      setTargets: (keys: Key[]) =>
        setFunctionTargets((prev) => ({ ...prev, function: keys })),
      onChange: (keys: Key[]) =>
        setFunctionTargets((prev) => ({ ...prev, function: keys })),
    },
    tpsp: {
      dataSource: tpspDataSource['tpsp'] || [],
      targetKeys: tpspTargets['tpsp'] || [],
      setTargets: (keys: Key[]) =>
        setTpspTargets((prev) => ({ ...prev, tpsp: keys })),
      onChange: (keys: Key[]) =>
        setTpspTargets((prev) => ({ ...prev, tpsp: keys })),
    },
    contractualArrangements: {
      dataSource:
        contractualArrangementsDataSource['contractualArrangements'] || [],
      targetKeys:
        contractualArrangementsTargets['contractualArrangements'] || [],
      setTargets: (keys: Key[]) =>
        setContractualArrangementsTargets((prev) => ({
          ...prev,
          contractualArrangements: keys,
        })),
      onChange: (keys: Key[]) =>
        setContractualArrangementsTargets((prev) => ({
          ...prev,
          contractualArrangements: keys,
        })),
    },
    ictSystems: {
      dataSource: ictSystemsDataSource['ictSystems'] || [],
      targetKeys: ictSystemsTargets['ictSystems'] || [],
      setTargets: (keys: Key[]) =>
        setIctSystemsTargets((prev) => ({ ...prev, ictSystems: keys })),
      onChange: (keys: Key[]) =>
        setIctSystemsTargets((prev) => ({ ...prev, ictSystems: keys })),
    },
    ictServices: {
      dataSource: ictServicesDataSource['ictServices'] || [],
      targetKeys: ictServicesTargets['ictServices'] || [],
      setTargets: (keys: Key[]) =>
        setIctServicesTargets((prev) => ({ ...prev, ictServices: keys })),
      onChange: (keys: Key[]) =>
        setIctServicesTargets((prev) => ({ ...prev, ictServices: keys })),
    },
  };

  const getTransferConfig = (configId: string) =>
    transferConfigMap[configId as keyof typeof transferConfigMap];

  const handleParentChange = async (
    id: string,
    value: number,
    label: string,
  ) => {
    setSelectedParents((p) => ({
      ...p,
      [id]: { id: value, label },
    }));

    if (
      id === 'branch' ||
      id === 'function' ||
      id === 'tpsp' ||
      id === 'contractualArrangements' ||
      id === 'ictSystems' ||
      id === 'ictServices'
    ) {
      await fetchDataByType(id, id);
      await loadAllocation(
        id as
          | 'branch'
          | 'function'
          | 'tpsp'
          | 'contractualArrangements'
          | 'ictSystems'
          | 'ictServices',
        value,
      );
    }
  };

  const loadEntityAllocation = async (financialId: number) => {
    const res = await entityOp.filterEntitybyFinancialEntities(financialId);

    const apiItems = (res?.data || []).map((e: any) => ({
      key: String(e.id),
      title: e.name_of_entity?.trim(),
      ...e,
    }));

    setDataSource((prev) => {
      const existing = prev.entity || [];

      const mergedMap = new Map();

      [...existing, ...apiItems].forEach((item) => {
        mergedMap.set(item.key, item);
      });

      return {
        ...prev,
        entity: Array.from(mergedMap.values()),
      };
    });
  };

  const { save } = useSaveAllocation({
    onSetLoading: (id, loading) =>
      setLoading((prev) => ({ ...prev, [id]: loading })),
  });

  const hasItemsToSave = (id: string): boolean => {
    const targetMap: Record<string, TargetConfig> = {
      branch: { targets: branchTargets, dataSource: branchDataSource },
      function: { targets: functionTargets, dataSource: functionDataSource },
      tpsp: { targets: tpspTargets, dataSource: tpspDataSource },
      contractualArrangements: {
        targets: contractualArrangementsTargets,
        dataSource: contractualArrangementsDataSource,
      },
      ictSystems: {
        targets: ictSystemsTargets,
        dataSource: ictSystemsDataSource,
      },
      ictServices: {
        targets: ictServicesTargets,
        dataSource: ictServicesDataSource,
      },
    };

    const config = targetMap[id];
    const currentTargets = config?.targets[id] || targets[id] || [];
    const previousTargets = previousTargetsRef.current[id] || [];

    if (currentTargets.length > 0) return true;

    const removed = previousTargets.filter(
      (key) => !currentTargets.includes(key),
    );
    return removed.length > 0;
  };

  const handleSave = async (id: string) => {
    const targetMap: Record<string, TargetConfig> = {
      branch: { targets: branchTargets, dataSource: branchDataSource },
      function: { targets: functionTargets, dataSource: functionDataSource },
      tpsp: { targets: tpspTargets, dataSource: tpspDataSource },
      contractualArrangements: {
        targets: contractualArrangementsTargets,
        dataSource: contractualArrangementsDataSource,
      },
      ictSystems: {
        targets: ictSystemsTargets,
        dataSource: ictSystemsDataSource,
      },
      ictServices: {
        targets: ictServicesTargets,
        dataSource: ictServicesDataSource,
      },
    };

    const config = targetMap[id];
    const currentTargets = config?.targets[id] || targets[id] || [];
    const previousTargets = previousTargetsRef.current[id] || [];

    const addedItems = currentTargets.filter(
      (key) => !previousTargets.includes(key),
    );
    const removedItems = previousTargets.filter(
      (key) => !currentTargets.includes(key),
    );

    if (!addedItems.length && !removedItems.length) {
      message.warning(t('ALLOCATION.NO_ITEMS_TO_SAVE'));
      return;
    }

    if (id === 'entity') {
      const token = localStorage.getItem('authToken') ?? '';
      const selectedItems = currentTargets
        .map((key: Key) =>
          (config?.dataSource || dataSource)[id]?.find(
            (item) => item.key === key,
          ),
        )
        .filter((item): item is AllocationItem => Boolean(item));

      const fullEntities = await Promise.all(
        selectedItems.map(async (item: any) => {
          const response = await entityOp.fetchEntitiesByEntityName({
            entity_id: Number(item.key),
            entity_name: item.title,
          });
          return response?.data;
        }),
      );
      const payload = fullEntities.map((data: any) => ({
        ...data,
        id: data.entity_id,
        name_of_entity: data.entity_name,
        name_of_financial_entity: selectedParents[id].label,
        financial_entity_lei_id: selectedParents[id].id,
      }));
      await entityOp.fetchEntity(payload, token);
      message.success(ALLOCATION_SUCCESS_MESSAGES[id]);
      await loadEntityAllocation(selectedParents[id].id);
    } else {
      const savePromises = [];

      // Save left-to-right (added items)
      if (addedItems.length > 0) {
        const addedItemsData = addedItems
          .map((key: Key) =>
            (config?.dataSource || dataSource)[id]?.find(
              (item) => item.key === key,
            ),
          )
          .filter((item): item is AllocationItem => Boolean(item));

        savePromises.push(
          save({
            id,
            parent: selectedParents[id],
            selectedItems: addedItemsData,
            dataSource: [],
            successMessage: ALLOCATION_SUCCESS_MESSAGES[id],
            isRightToLeft: false,
          }),
        );
      }

      // Save right-to-left (removed items)
      if (removedItems.length > 0) {
        const removedItemsData = removedItems
          .map((key: Key) =>
            (config?.dataSource || dataSource)[id]?.find(
              (item) => item.key === key,
            ),
          )
          .filter((item): item is AllocationItem => Boolean(item));

        savePromises.push(
          save({
            id,
            parent: selectedParents[id],
            selectedItems: removedItemsData,
            dataSource: [],
            successMessage: ALLOCATION_SUCCESS_MESSAGES[id],
            isRightToLeft: true,
          }),
        );
      }

      // Execute both saves simultaneously
      await Promise.all(savePromises);
      await fetchDataByType(id, id);
      await loadAllocation(id as any, selectedParents[id].id);
      const currentTargetsAfterSave =
        id === 'function'
          ? functionTargets[id]
          : id === 'branch'
            ? branchTargets[id]
            : id === 'tpsp'
              ? tpspTargets[id]
              : id === 'contractualArrangements'
                ? contractualArrangementsTargets[id]
                : id === 'ictSystems'
                  ? ictSystemsTargets[id]
                  : id === 'ictServices'
                    ? ictServicesTargets[id]
                    : targets[id];
      previousTargetsRef.current[id] = currentTargetsAfterSave || [];
    }
  };

  return (
    <Styled.ContentWrapper>
      <Row gutter={[24, 24]}>
        {allocationConfigs.map((c) => {
          const isDisabled = c.hasParent
            ? !selectedParents[c.id]
            : !c.dependsOn || !(targets[c.dependsOn] || []).length;

          return (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} key={c.id}>
              <Styled.TransferCard
                title={c.title}
                extra={
                  (c.type === 'entity' ||
                    c.type === 'function' ||
                    c.type === 'branch' ||
                    c.type === 'tpsp' ||
                    c.type === 'contractualArrangements' ||
                    c.type === 'ictSystems' ||
                    c.type === 'ictServices') && (
                    <Button
                      type="primary"
                      disabled={!selectedParents[c.id] || !hasItemsToSave(c.id)}
                      loading={loading[c.id]}
                      onClick={() => handleSave(c.id)}
                    >
                      {t('ALLOCATION.SAVE')}
                    </Button>
                  )
                }
              >
                {c.hasParent && (
                  <Select
                    placeholder={t('ALLOCATION.SELECT_PARENT')}
                    onChange={(value, option: any) => {
                      handleParentChange(c.id, value, option.label);
                    }}
                    options={c.parentOptions}
                  />
                )}

                {(() => {
                  const config = getTransferConfig(c.id);
                  return (
                    <Transfer
                      dataSource={config.dataSource}
                      targetKeys={config.targetKeys}
                      onChange={config.onChange}
                      render={(item) => item.title}
                      showSearch
                      disabled={isDisabled}
                      listStyle={{
                        width: '100%',
                        maxWidth: 400,
                        height: 280,
                        marginTop: 16,
                      }}
                    />
                  );
                })()}
              </Styled.TransferCard>
            </Col>
          );
        })}
      </Row>
    </Styled.ContentWrapper>
  );
};

export default Allocation;
